import React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Query } from 'react-apollo';
import { useMutation } from '@apollo/react-hooks';
import Tooltip from '@material-ui/core/Tooltip';
import { GET_STUDENT_CREDITS, ADD_CREDIT } from 'graphql/credits';
import { toUserTimezone } from 'utils';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

const TYPES = {
  hc: 'Head Counselor Session',
  cc: 'College Coach Session',
  fa: 'Financial Aid Session',
  cc_essay: 'CC Essay Credit',
  hc_essay: 'HC Essay Credit',
  fafsa: 'FAFSA Credit',
  css_profile: 'CSS Profile Credit',
  fafsa_css: 'FAFSA + CSS Credit',
  expert: 'Expert Credit',
  circle_hc: 'Head Counselor Session',
  circle_cc: 'College Coach Session',
};

const amountStyle = {
  credit: { color: 'black ' },
  debit: { color: 'red' },
};

const useStyles = makeStyles((theme) => ({
  subheading: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  caption: {
    color: '#AAB5CE',
    paddingLeft: 10,
    paddingRight: 10,
  },
}));

const Credits = ({ student }) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const [addMutation] = useMutation(ADD_CREDIT);
  const columns = [
    {
      title: 'Date',
      field: 'created_at',
      render: (row) =>
        toUserTimezone(row.created_at, user.timwzone).format('L'),
      type: 'date',
      editable: 'onAdd',
    },
    {
      title: 'Type',
      field: 'credit_type',
      lookup: TYPES,
      editable: 'onAdd',
      initialEditValue: undefined,
    },
    {
      title: 'Description',
      field: 'description',
      editable: 'onAdd',
      lookup: {
        'Credit usage': 'Credit usage',
        'Credit adjustment': 'Credit adjustment',
        'Referral bonus': 'Referral bonus'
      }
    },
    {
      title: 'Amount',
      field: 'amount',
      type: 'numeric',
      cellStyle: (val) => (val < 0 ? amountStyle.debit : amountStyle.credit),
      editable: 'onAdd',
    },
  ];

  return (
    <div>
      <Helmet title="Credits" />
      <Query
        query={GET_STUDENT_CREDITS}
        variables={{ userId: student.id }}
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, refetch }) => {
          const meeting_credits = data?.meeting_credits;
          const usage = data?.usage;

          const onRowAdd = (values) => {
            if (values.description === 'Credit usage') {
              values.amount = -Math.abs(values.amount);
            }
            return addMutation({
              variables: {
                input: {
                  ...values,
                  user_id: student.id,
                },
              },
            }).then(() => refetch());
          };

          const circle_cc_total = usage?.find(i => i.credit_type === 'circle_cc')?.available || 0;
          const circle_cc_done = Math.abs(usage?.find(i => i.credit_type === 'circle_cc')?.debit || 0);
          let cc_total = usage?.find(i => i.credit_type === 'cc')?.available || 0;
          let cc_done = Math.abs(usage?.find(i => i.credit_type === 'cc')?.debit || 0);
          cc_total = cc_total + circle_cc_total
          cc_done = cc_done + circle_cc_done

          const circle_hc_total = usage?.find(i => i.credit_type === 'circle_hc')?.available || 0;
          const circle_hc_done = Math.abs(usage?.find(i => i.credit_type === 'circle_hc')?.debit || 0);
          let hc_total = usage?.find(i => i.credit_type === 'hc')?.available || 0;
          let hc_done = Math.abs(usage?.find(i => i.credit_type === 'hc')?.debit || 0);
          hc_total = hc_total + circle_hc_total
          hc_done = hc_done + circle_hc_done

          const fa_total = usage?.find(i => i.credit_type === 'fa')?.available || 0;
          const fa_done = Math.abs(usage?.find(i => i.credit_type === 'fa')?.debit || 0);

          const cc_essay_total = usage?.find(i => i.credit_type === 'cc_essay')?.available || 0;
          const cc_essay_done = Math.abs(usage?.find(i => i.credit_type === 'cc_essay')?.debit || 0);
          const hc_essay_total = usage?.find(i => i.credit_type === 'hc_essay')?.available || 0;
          const hc_essay_done = Math.abs(usage?.find(i => i.credit_type === 'hc_essay')?.debit || 0);

          const fafsa_total = usage?.find(i => i.credit_type === 'fafsa')?.available || 0;
          const fafsa_done = Math.abs(usage?.find(i => i.credit_type === 'fafsa')?.debit || 0);
          const cssp_total = usage?.find(i => i.credit_type === 'css_profile')?.available || 0;
          const cssp_done = Math.abs(usage?.find(i => i.credit_type === 'css_profile')?.debit || 0);
          const fafsaCss_total = usage?.find(i => i.credit_type === 'fafsa_css')?.available || 0;
          const fafsaCss_done = Math.abs(usage?.find(i => i.credit_type === 'fafsa_css')?.debit || 0);

          const expert_total = usage?.find(i => i.credit_type === 'expert')?.available || 0;
          const expert_done = Math.abs(usage?.find(i => i.credit_type === 'expert')?.debit || 0)

          return (
            <div style={{ width: 800 }}>
              <Grid container spacing={3}>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {cc_done}/{cc_total}
                  </Typography>
                  <Typography className={classes.subheading}>College Coach</Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {hc_done}/{hc_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    Head Counselor
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {fa_done}/{fa_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    Financial Aid
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {cc_essay_done}/{cc_essay_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    CC Essay Credits
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {hc_essay_done}/{hc_essay_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    HC Essay Credits
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {fafsa_done}/{fafsa_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    FAFSA Credits
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {cssp_done}/{cssp_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    CSS Profile Credits
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {fafsaCss_done}/{fafsaCss_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    FAFSA + CSS Credits
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {expert_done}/{expert_total}
                  </Typography>
                  <Typography className={classes.subheading}>
                    Expert Credits
                  </Typography>
                </Grid>
              </Grid>
              <MaterialTable
                title="Credits Ledger"
                isLoading={loading}
                options={{
                  search: false,
                  paging: false,
                  addRowPosition: 'first',
                  actionsColumnIndex: -1,
                }}
                columns={columns}
                data={meeting_credits}
                editable={{
                  onRowAdd,
                }}
                actions={[]}
              />
            </div>
          );
        }}
      </Query>
    </div>
  );
};

export default Credits;
